import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, isRef as _isRef, withDirectives as _withDirectives, withModifiers as _withModifiers, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = { class: "form-wrapper" }
const _hoisted_3 = { class: "header" }
const _hoisted_4 = { class: "studentInformationForm draws-tab" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "xform-section" }
const _hoisted_7 = { class: "xfields xfield-group" }
const _hoisted_8 = { class: "xfields xfield-group" }
const _hoisted_9 = { style: {"display":"none"} }
const _hoisted_10 = ["disabled"]

import { computed, ref, watch, onMounted } from "vue";
import { $api } from "@/services/api1";
import { useRoute, useRouter } from "vue-router";
import store from "@/store";
import functions from "@/use/functions";
import { useStudentFormFields } from "@/composables/student/useStudentFormFields";
import { Permissions, PermissionLevels } from "@/models/permissions";
import { FormModes } from "@/types/form";
import { ConsentServiceEntityTypeIds } from "@/types/consents";
import { Field as VeeField } from "vee-validate";
import BaseFormField from "@/components/form-fields/BaseFormField.vue";
import AddressInput from "@/components/v2/application/forms/AddressInput.vue";
import CollapsibleSection from "@/components/v2/transitions/CollapsibleSection.vue";
import NationalIdNumberInput from "@/components/inputs/NationalIdNumberInput.vue";
import FloatingDots from "@/components/loaders/FloatingDots.vue";
import ParentAttestationConsent from "@/components/v2/consents/ParentAttestationConsent.vue";
import { AddressTypeIds } from "@/types/address";
import { getOptionLabel } from "@/composables/application/useApplicationForms";
import type { ApplicantAddress } from "@/types/applicant";
import type { LoanDetails } from "@/models/loans";
import type { BorrowerAddressModel } from "@/models/loans";
import type { StudentFormVM } from "@/types/student";
import type { UpdateBorrowerRequest } from "@/models/loans";
import type { RootStore } from "@/types/root-store";
import type { FormChange, FormSchema, FormPatch } from "@/models/form";
import type { KeyRename, CompareOptions } from "@/types/types";
import { usePermissionsAccess } from "@/composables/usePermissionsAccess";
import { NationalIdNumberTypeIds } from "@/types/application-details";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentForm',
  props: {
    loanDetails: {},
    draw: {},
    student: {},
    useApplicationLoanId: { type: Boolean, default: false },
    permissionIds: { default: () => [] },
    permissionLevel: { default: PermissionLevels.None }
  },
  emits: ["studentUpdated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const emit = __emit;

const addressInputRef = ref<InstanceType<typeof AddressInput>>();
const parentAttestationConsentRef =
  ref<InstanceType<typeof ParentAttestationConsent>>();

const route = useRoute();
const router = useRouter();

const props = __props;

const permissionsAccess = usePermissionsAccess();

let person = ref({
  id: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  citizenshipStatusId: 0,
  citizenshipStatusName: "",
  nationalIdNumberTypeId: 0,
  nationalIdNumber: "",
  parentConsent: undefined as undefined | boolean,
  parentAttestation: undefined as undefined | any,
  address: {} as ApplicantAddress,
  itinIndicator: undefined as undefined | boolean,
});

//#TODO
const gettingData = ref<boolean>(true);
const mode = ref<keyof typeof FormModes>(FormModes.view);
const entityTypeId = ref<number>(ConsentServiceEntityTypeIds.Draw);
const savingChanges = ref<boolean>(false);
const showAdditionalInfo = ref<boolean>(false);
const studentBackup = ref<StudentFormVM | undefined>(undefined);
const showUnitType = ref<boolean>(false);

const {
  buildStudentFormChangesTable,
  changeMode,
  citizenshipStatusOptions,
  confirmChangesTitle,
  errorModalTitle,
  errors,
  formatDateOfBirth,
  generatePatchPath,
  generatePathOp,
  getStudentInformationFormAsync,
  handleFieldChange,
  nationalIdNumberLabel,
  schema,
  setValues,
  setFieldValue,
  values,
} = useStudentFormFields({
  studentInfo: person,
  mode,
  entityTypeId,
  useAddressInputComponent: true,
});

const canUpdate = computed<boolean>(() => {
  const permissionIdsValue = [Permissions.Draws];

  return permissionsAccess.hasPermission(
    permissionIdsValue,
    PermissionLevels.Update
  );
});

const hasErrors = computed<boolean>(
  () => errors.value && Object.keys(errors.value).length > 0
);

const addressSchema = computed<FormSchema | undefined>(
  () => addressInputRef.value?.addressSchema
);

const validAddressInput = computed<boolean>(
  () => (addressInputRef.value && !addressInputRef.value?.hasErrors) === true
);

const skipKeys = computed<string[]>(() => {
  return ["suffixTypeId", "applicationLoanId", "id", "applicantId", "userId"];
});

const keysToRename = computed<KeyRename[]>(() => {
  return [
    {
      key: "nationalIdNumber",
      rename: nationalIdNumberLabel.value || "",
    } as KeyRename,
    {
      key: "dateOfBirth",
      rename: schema?.value.dateOfBirth.label,
    } as KeyRename,
    {
      key: "citizenshipStatusTypeId",
      rename: schema.value.citizenshipStatusId.label,
    } as KeyRename,
    {
      key: "citizenshipStatusId",
      rename: schema.value.citizenshipStatusId.label,
    } as KeyRename,
    {
      key: "address1",
      rename: addressSchema.value?.address1?.label,
    } as KeyRename,
    {
      key: "address2",
      rename: addressSchema.value?.address2.label,
    } as KeyRename,
    {
      key: "city",
      rename: addressSchema.value?.city.label,
    } as KeyRename,
    {
      key: "stateName",
      rename: addressSchema.value?.stateId.label,
    } as KeyRename,
    {
      key: "postalCode",
      rename: addressSchema.value?.postalCode.label,
    } as KeyRename,
    {
      key: "countryName",
      rename: addressSchema.value?.countryId.label,
    } as KeyRename,
    {
      key: "addressUnitTypeName",
      rename: addressSchema.value?.addressUnitTypeId?.label,
    } as KeyRename,
  ];
});

const originalBorrower = computed<UpdateBorrowerRequest>(() => {
  const cleanedPostalCode = props.student?.address?.postalCode.replace(
    /[^0-9]/g,
    ""
  );
  const postalCode =
    cleanedPostalCode && cleanedPostalCode.length === 9
      ? cleanedPostalCode.slice(0, 5)
      : cleanedPostalCode;

  const postalCodeExtension =
    cleanedPostalCode && cleanedPostalCode.length === 9
      ? cleanedPostalCode.slice(-4)
      : null;

  return {
    loanId: props.useApplicationLoanId
      ? props.loanDetails.applicationLoanId || props.loanDetails.loanId
      : props.loanDetails.loanId,
    borrowerId: props.student?.id,
    firstName: props.student?.firstName?.trim(),
    lastName: props.student?.lastName?.trim(),
    dateOfBirth: props.student?.dateOfBirth + "T00:00:00",
    citizenshipStatusTypeId: props.student?.citizenshipStatusId,
    suffixTypeId: null,
    borrowerEncryptedData: {
      nationalIdNumber: props.student?.nationalIdNumber,
      nationalIdNumberTypeId: props.student?.nationalIdNumberTypeId,
    },
    borrowerAddress: {
      addressTypeId: AddressTypeIds.PERMANENT,
      addressStateId: props.student?.address?.stateId,
      addressCountryId: props.student?.address?.countryId,
      addressLineOne: props.student?.address?.address1,
      addressLineTwo: props.student?.address?.address2,
      city: props.student?.address?.city,
      postalCode: postalCode,
      postalCodeExtension: postalCodeExtension,
    },
    borrowerContact: null, // ignored
    borrowerDriversLicense: null, //ignore
  } as UpdateBorrowerRequest;
});

const updateBorrowerRequest = computed<UpdateBorrowerRequest>(() => {
  return {
    loanId: props.useApplicationLoanId
      ? props.loanDetails.applicationLoanId || props.loanDetails.loanId
      : props.loanDetails.loanId,
    borrowerId: props.student?.id,
    firstName: person.value.firstName?.trim(),
    lastName: person.value.lastName?.trim(),
    dateOfBirth: person.value.dateOfBirth + "T00:00:00",
    citizenshipStatusTypeId: Number(person.value?.citizenshipStatusId),
    suffixTypeId: null,
    borrowerEncryptedData: {
      nationalIdNumber: person.value.nationalIdNumber,
      nationalIdNumberTypeId:
        person.value.itinIndicator === true
          ? NationalIdNumberTypeIds.ITIN
          : NationalIdNumberTypeIds.SSN,
    },
    borrowerAddress: {
      addressTypeId: AddressTypeIds.PERMANENT,
      addressStateId: Number(addressInputRef.value?.values?.stateId),
      addressCountryId: Number(addressInputRef.value?.values?.countryId),
      addressLineOne: addressInputRef.value?.values?.address1,
      addressLineTwo: showUnitType.value
        ? addressInputRef.value?.formattedAddress2
        : addressInputRef.value?.values?.address2,
      city: addressInputRef.value?.address?.city,
      postalCode: addressInputRef.value?.postalCode,
      postalCodeExtension: addressInputRef.value?.postalCodeExtension,
    },
    borrowerContact: null, // ignored
    borrowerDriversLicense: null, //ignore
  } as UpdateBorrowerRequest;
});

const updatedBorrowerAddress = computed<BorrowerAddressModel>(() => {
  return {
    id: props.student?.id || 0,
    addressTypeId:
      props?.student?.address?.addressTypeId ||
      addressInputRef.value?.values?.addressTypeId ||
      0,
    addressStateId:
      addressInputRef.value?.values?.stateId ||
      props?.student?.address?.stateId ||
      0,
    addressCountryId: addressInputRef.value?.values?.countryId || 0,
    addressLineOne: addressInputRef.value?.values?.address1 || "",
    addressLineTwo: showUnitType.value
      ? addressInputRef.value?.formattedAddress2 || null
      : addressInputRef.value?.values?.address2 || null,
    city: addressInputRef.value?.values?.city || "",
    postalCode: addressInputRef.value?.postalCode || "",
    postalCodeExtension: addressInputRef.value?.postalCodeExtension || null,
  };
});

const updatedAddress = computed<ApplicantAddress>(() => {
  return {
    address1: addressInputRef.value?.values?.address1 || "",
    address2: showUnitType.value
      ? addressInputRef.value?.formattedAddress2 || null
      : addressInputRef.value?.values?.address2 || null,
    addressId: addressInputRef.value?.values?.addressId || 0,
    addressTypeId: addressInputRef.value?.values?.addressTypeId || 0,
    addressTypeName: addressInputRef.value?.values?.addressTypeName || "",
    addressUnitTypeId: addressInputRef.value?.values?.addressUnitTypeId || null,
    addressUnitTypeName:
      addressInputRef.value?.values?.addressUnitTypeName || null,
    applicantId: addressInputRef.value?.values?.applicantId || 0,
    id: addressInputRef.value?.values?.id || 0,
    city: addressInputRef.value?.values?.city || "",
    stateId: addressInputRef.value?.values?.stateId || 0,
    stateName: addressInputRef.value?.values?.stateName || "",
    postalCode: addressInputRef.value?.values?.postalCode || "",
    countryId: addressInputRef.value?.values?.countryId || 0,
    countryName: addressInputRef.value?.values?.countryName || "",
  };
});

watch(
  () => updatedAddress.value,
  (newVal) => {
    person.value.address = newVal;
    setFieldValue("address", newVal);
  },
  {
    deep: true,
  }
);
function confirmChanges() {
  // Validate that the SSN and DOB are not the same as parent
  const options = {
    programTypeId: 0,
    productTypeId: 0,
    keysToSkip: skipKeys.value,
    keysToPick: [],
    keysToRename: keysToRename.value,
    keysToFormat: [],
    fieldsToCompare: [],
  } as CompareOptions;

  const formChanges = checkStudentModelChanges(
    updateBorrowerRequest.value,
    originalBorrower.value,
    options
  );

  const modalHtml = buildStudentFormChangesTable(formChanges.changes);

  console.log("formChanges", formChanges);
  console.log("modalHtml", { modalHtml });

  if (formChanges.hasChanges) {
    functions.openModal({
      title: confirmChangesTitle.value,
      description: "Please review your changes before submitting...",
      html: modalHtml,
      buttons: [
        { title: "Cancel", onClick: () => functions.closeModal() },
        {
          title: "Save Changes",
          onClick: async () => {
            functions.setModalLoading(true);
            await updateStudentInformation(formChanges.patches);
          },
        },
      ],
    });
  } else {
    resetSchemas();
    changeMode(FormModes.view);
    savingChanges.value = false;
    functions.setModalLoading(false);
    functions.closeModal();
  }
}

function submit() {
  savingChanges.value = true;

  if (!hasErrors.value && validAddressInput.value) {
    confirmChanges();
  } else {
    savingChanges.value = false;
  }
}

function resetSchemas() {
  for (const property in schema.value) {
    schema.value[property].valid = true;
    schema.value[property].errors = [];
    schema.value[property].touched = false;
  }

  addressInputRef.value?.resetSchemas();
}

function resetForm() {
  person.value = {
    id: 0,
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    citizenshipStatusId: 0,
    citizenshipStatusName: "",
    nationalIdNumberTypeId: 0,
    nationalIdNumber: "",
    parentConsent: undefined,
    parentAttestation: {},
    address: {} as ApplicantAddress,
    itinIndicator: undefined,
  };

  studentBackup.value = JSON.parse(JSON.stringify(person.value));
  person.value = JSON.parse(JSON.stringify(studentBackup.value));
}

function discardChanges() {
  if (!props.student) {
    resetForm();
    resetSchemas();
    addressInputRef.value?.discardChanges();
    return;
  }

  person.value = JSON.parse(JSON.stringify(studentBackup.value));
  studentBackup.value = JSON.parse(JSON.stringify(person.value));

  setStudentInformation();
  resetSchemas();

  addressInputRef.value?.discardChanges();
}

function handleCancelButtonClick() {
  discardChanges();
  changeMode(FormModes.view);
}

function setStudentInformation() {
  if (props.student !== undefined) {
    studentBackup.value = JSON.parse(JSON.stringify(props.student));

    person.value = {
      id: props.student?.id || 0,
      firstName: props.student?.firstName || "",
      lastName: props.student?.lastName || "",
      dateOfBirth: props.student?.dateOfBirth || "",
      citizenshipStatusId: props.student?.citizenshipStatusId || 0,
      citizenshipStatusName: props.student?.citizenshipStatusName,
      nationalIdNumber: props.student?.nationalIdNumber,
      nationalIdNumberTypeId: props.student?.nationalIdNumberTypeId,
      parentAttestation: props.student?.parentAttestation,
      parentConsent: props.student?.parentAttestation?.consentGiven,
      address: props.student?.address,
      itinIndicator:
        props.student.nationalIdNumberTypeId === NationalIdNumberTypeIds.ITIN,
    };

    setValues(person.value);

    // Set adddress
    if (props.student?.address) {
      addressInputRef.value?.setValues(props.student?.address);
    }
  }
}

function checkStudentModelChanges(
  object1: UpdateBorrowerRequest,
  object2: UpdateBorrowerRequest,
  options: CompareOptions
) {
  options.pathPrefix = undefined;

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  let html = "";

  const patches = [] as FormPatch[];
  const changes = [] as FormChange[];

  functions.compareObjects(object1, object2).forEach((reference) => {
    let before = reference.reduce((o, key) => o[key], object2);
    let after = reference.reduce((o, key) => o[key], object1);

    const path = options?.pathPrefix
      ? options?.pathPrefix + generatePatchPath(reference)
      : generatePatchPath(reference);

    // get the last variable from the reference array
    let key = reference[reference.length - 1];

    // get the last variable from the reference array, and convert the camelCase to add spaces
    let name = reference[reference.length - 1]?.replace(
      /([a-z])([A-Z])/g,
      `$1 $2`
    );

    // if (options?.keysToSkip?.includes(key)) {
    //   return;
    // }

    let rename = options?.keysToRename?.find(
      (k: KeyRename) => k.key == key
    )?.rename;

    if (rename) {
      name = rename;
    }

    if (before === undefined || before === null || before === "") {
      before = null;
    }

    if (after === undefined || after === null || after === "") {
      after = null;
    }

    if (before === after) {
      // do not add to table if the values are the same
      return;
    }

    patches.push({
      op: generatePathOp(before, after),
      path: path,
      value: after,
    });

    if (before === null) {
      before = "N/A";
    }

    if (after === null) {
      after = "N/A";
    }

    if (key === "dateOfBirth") {
      before = formatDateOfBirth(before?.split("T")[0]);
      after = formatDateOfBirth(after?.split("T")[0]);
    }

    // handle display values
    if (key === "addressStateTypeId" || key === "addressStateId") {
      name = addressSchema.value?.stateId?.label;
      before =
        before !== "N/A" && addressInputRef.value?.statesOptions
          ? getOptionLabel(addressInputRef.value?.statesOptions, Number(before))
          : before;
      after =
        after !== "N/A" && addressInputRef.value?.statesOptions
          ? getOptionLabel(addressInputRef.value?.statesOptions, Number(after))
          : after;
    }

    if (key === "addressCountryTypeId" || key === "addressCountryId") {
      name = addressSchema.value?.countryId?.label;
      before =
        before !== "N/A" && addressInputRef.value?.countryOptions
          ? getOptionLabel(
              addressInputRef.value?.countryOptions,
              Number(before)
            )
          : before;
      after =
        after !== "N/A" && addressInputRef.value?.countryOptions
          ? getOptionLabel(addressInputRef.value?.countryOptions, Number(after))
          : after;
    }

    if (key === "addressLineOne") {
      name = addressSchema.value?.address1?.label;
    }

    if (key === "addressLineTwo") {
      name = addressSchema.value?.address2?.label;
    }

    if (key === "city") {
      name = addressSchema.value?.city?.label;
    }

    if (key === "citizenshipStatusTypeId") {
      name = schema.value?.citizenshipStatusId?.label;
      before =
        before !== "N/A" && citizenshipStatusOptions.value
          ? getOptionLabel(citizenshipStatusOptions.value, Number(before))
          : before;
      after =
        after !== "N/A" && citizenshipStatusOptions.value
          ? getOptionLabel(citizenshipStatusOptions.value, Number(after))
          : after;
    }

    if (!options?.keysToSkip?.includes(key)) {
      changes.push({
        key: key,
        name: name,
        label: name,
        before: before,
        after: after,
        value: patches.find((p) => p.path)?.value,
        comment: `${functions.capitalize(name)} : ${after}`,
      });

      html += `<tr><td class="xbefore">${name}</td><td class="xafter">${before}</td><td class="xafter">${after}</td></tr>`;
    }
  });

  const filteredChanges = changes.filter(
    (change) =>
      !(options?.keysToExcludeFromSummaryComment || []).includes(change.key)
  );

  const lastIndex = filteredChanges.length - 1;

  const changeSummaries = filteredChanges
    .map((change, index) => {
      if (index === lastIndex) {
        return change.comment + ".";
      } else if (index === 0) {
        return change.comment + " | ";
      } else {
        return change.comment + " " + " | ";
      }
    })
    .join("");

  const fieldsChanged = filteredChanges
    .map((change, index) => {
      if (filteredChanges.length === 1) {
        return change.name;
      } else if (filteredChanges.length === 2) {
        return index === lastIndex ? change.name : `${change.name} and `;
      } else {
        if (index === lastIndex) {
          return `and ${change.name}`;
        } else {
          return change.name + ", ";
        }
      }
    })
    .join("");

  const errorMessage = `${fieldsChanged} change${
    filteredChanges.length > 1 ? "s" : ""
  } could not be saved.`;

  const comment = `${options?.commentPrefix} ${changeSummaries}`;

  return {
    patches,
    changes,
    html,
    hasChanges: patches.length > 0,
    errorMessage,
    comment,
  };
}

async function updateStudentInformation(operations: FormPatch[]) {
  // filter out nationalIdnumber if it's the masked value
  // Follows XXX-XX-#### format
  const masked = /^X{3}-X{2}-\d{4}$/.test(person.value?.nationalIdNumber);
  let request = {
    ...updateBorrowerRequest.value,
  } as UpdateBorrowerRequest;

  if (masked) {
    request.borrowerEncryptedData = {
      nationalIdNumber: null,
      nationalIdNumberTypeId:
        updateBorrowerRequest.value.borrowerEncryptedData
          ?.nationalIdNumberTypeId || null,
    };

    operations = operations.filter(
      (o) => o.path !== "/student/nationalIdNumber"
    );
  }
  console.log("patch ops", operations);

  const response = await $api.loans.updateBorrowerAsync(
    props.loanDetails.loan.id,
    request
  );

  if (!response?.errors) {
    emit("studentUpdated", person.value?.id || 0);
    savingChanges.value = false;
  } else {
    savingChanges.value = false;
    functions.openModal({
      title: "Error",
      description: "There was an issue saving the Student",
    });
  }
}

async function initializeForm(): Promise<void> {
  gettingData.value = true;

  await getStudentInformationFormAsync(route.params.referenceId.toString());

  setStudentInformation();

  gettingData.value = false;
}

function toggleQuery(
  queryKey: string,
  flag?: boolean,
  shouldDelete: undefined | boolean = false
) {
  // 1. Create a shallow copy of the existing query parameters
  const query = { ...route.query };

  // 2. Toggle specific query key
  if (shouldDelete && query[queryKey]) {
    delete query[queryKey]; // Removes the key from the object
  } else {
    query[queryKey] = `${flag}`; // Adds the key to the object or updates it
  }

  // 3. Update the route without triggering a full page refresh and preserve hash
  router.replace({
    query: query,
    hash: route.hash, // Preserves the current hash (e.g., #section-2)
  });
}

watch(
  () => mode.value,
  () => {
    if (mode.value === FormModes.edit) {
      showAdditionalInfo.value = true;
    } else {
      for (const property in schema.value) {
        schema.value[property].valid = true;
        schema.value[property].errors = [];
        schema.value[property].touched = false;
      }
    }
  }
);

// watch for the modal to close
const confirmChangesModalActive = computed<boolean>(
  () =>
    (store.state as RootStore).modal.active &&
    (store.state as RootStore).modal.title === confirmChangesTitle.value
);

const errorModalActive = computed<boolean>(
  () =>
    (store.state as RootStore).modal.active &&
    (store.state as RootStore).modal.title === errorModalTitle.value
);

const StudentDetailsExpandedQueryParam = computed(
  () => route.query.studentDetailsExpanded
);

watch(
  () => showAdditionalInfo.value,
  () => {
    toggleQuery("studentDetailsExpanded", showAdditionalInfo.value);
  }
);

watch(
  () => confirmChangesModalActive.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      savingChanges.value = false;
    }
  }
);

watch(
  () => errorModalActive.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      savingChanges.value = false;
    }
  }
);
watch(
  () => props.student,
  async (newVal, oldVal) => {
    console.log("student changed", { oldVal, newVal });

    await initializeForm();
  },
  {
    deep: true,
  }
);
onMounted(async () => {
  await initializeForm();

  if (StudentDetailsExpandedQueryParam.value) {
    showAdditionalInfo.value =
      StudentDetailsExpandedQueryParam.value === "true" ? true : false;
  }
});

// watch for changes to the param and get latest student info form
watch(
  () => route.params.referenceId,
  async () => {
    await initializeForm();
  }
);

__expose({
  addressInputRef,
  changeMode,
  parentAttestationConsentRef,
  updateBorrowerRequest,
  updatedAddress,
  updatedBorrowerAddress,
  values,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (gettingData.value)
      ? (_openBlock(), _createBlock(FloatingDots, { key: 0 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[16] || (_cache[16] = _createElementVNode("h1", null, "Student Information", -1)),
        _createVNode(PermissionsWrapper, {
          permissionIds: [_unref(Permissions).Draws],
          permissionLevel: _unref(PermissionLevels).Update
        }, {
          default: _withCtx(() => [
            (mode.value === _unref(FormModes).view && !gettingData.value && canUpdate.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "submit-button col-12",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(changeMode)('edit')))
                }, " Edit "))
              : _createCommentVNode("", true),
            (mode.value === _unref(FormModes).edit && !gettingData.value && canUpdate.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: "submit-button col-12",
                  onClick: handleCancelButtonClick
                }, " Cancel "))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["permissionIds", "permissionLevel"])
      ]),
      _withDirectives(_createElementVNode("form", _hoisted_4, [
        _createElementVNode("fieldset", {
          disabled: mode.value === _unref(FormModes).view
        }, [
          _withDirectives(_createVNode(_unref(VeeField), {
            name: "person",
            modelValue: _unref(person),
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(person) ? (person).value = $event : person = $event))
          }, null, 8, ["modelValue"]), [
            [_vShow, false]
          ]),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createVNode(BaseFormField, {
                schema: _unref(schema).firstName,
                modelValue: _unref(person).firstName,
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => ((_unref(person).firstName) = $event)),
                  _cache[3] || (_cache[3] = ($event: any) => (_unref(handleFieldChange)('firstName', $event)))
                ]
              }, null, 8, ["schema", "modelValue"]),
              _createVNode(BaseFormField, {
                schema: _unref(schema).lastName,
                modelValue: _unref(person).lastName,
                "onUpdate:modelValue": [
                  _cache[4] || (_cache[4] = ($event: any) => ((_unref(person).lastName) = $event)),
                  _cache[5] || (_cache[5] = ($event: any) => (_unref(handleFieldChange)('lastName', $event)))
                ]
              }, null, 8, ["schema", "modelValue"]),
              _createVNode(BaseFormField, {
                schema: _unref(schema).dateOfBirth,
                modelValue: _unref(person).dateOfBirth,
                "onUpdate:modelValue": [
                  _cache[6] || (_cache[6] = ($event: any) => ((_unref(person).dateOfBirth) = $event)),
                  _cache[7] || (_cache[7] = ($event: any) => (_unref(handleFieldChange)('dateOfBirth', $event)))
                ]
              }, null, 8, ["schema", "modelValue"])
            ]),
            _createVNode(CollapsibleSection, {
              isVisible: showAdditionalInfo.value || mode.value === _unref(FormModes).edit
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(BaseFormField, {
                    schema: _unref(schema).citizenshipStatusId,
                    modelValue: _unref(person).citizenshipStatusId,
                    "onUpdate:modelValue": [
                      _cache[8] || (_cache[8] = ($event: any) => ((_unref(person).citizenshipStatusId) = $event)),
                      _cache[9] || (_cache[9] = ($event: any) => (
                    _unref(handleFieldChange)('citizenshipStatusId', $event)
                  ))
                    ],
                    modelModifiers: { number: true },
                    options: _unref(citizenshipStatusOptions)
                  }, null, 8, ["schema", "modelValue", "options"]),
                  _createVNode(NationalIdNumberInput, {
                    disabled: savingChanges.value || mode.value === _unref(FormModes).view,
                    modelValue: _unref(person).nationalIdNumber,
                    "onUpdate:modelValue": [
                      _cache[10] || (_cache[10] = ($event: any) => ((_unref(person).nationalIdNumber) = $event)),
                      _cache[12] || (_cache[12] = ($event: any) => (
                    _unref(handleFieldChange)('nationalIdNumber', $event)
                  ))
                    ],
                    permissionIds: [_unref(Permissions).ApplicantSSN],
                    permissionLevel: _unref(PermissionLevels).Update,
                    fieldErrors: 
                    _unref(errors).nationalIdNumber && mode.value === _unref(FormModes).edit
                      ? [_unref(errors).nationalIdNumber]
                      : []
                  ,
                    schema: _unref(schema).nationalIdNumber,
                    nationalIdNumberTypeId: _unref(person).nationalIdNumberTypeId,
                    "onUpdate:itinIndicator": _cache[11] || (_cache[11] = ($event: any) => (
                    _unref(handleFieldChange)('itinIndicator', $event)
                  ))
                  }, null, 8, ["disabled", "modelValue", "permissionIds", "permissionLevel", "fieldErrors", "schema", "nationalIdNumberTypeId"]),
                  _createVNode(AddressInput, {
                    ref_key: "addressInputRef",
                    ref: addressInputRef,
                    initialValues: __props.student?.address || {},
                    disabled: mode.value === _unref(FormModes).view,
                    mode: mode.value,
                    header: "Student Address",
                    showHeader: false,
                    entityTypeId: _unref(ConsentServiceEntityTypeIds).Draw,
                    showUnitType: showUnitType.value
                  }, null, 8, ["initialValues", "disabled", "mode", "entityTypeId", "showUnitType"])
                ])
              ]),
              _: 1
            }, 8, ["isVisible"]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(ParentAttestationConsent, {
                ref_key: "parentAttestationConsentRef",
                ref: parentAttestationConsentRef,
                modelValue: _unref(person).parentConsent,
                "onUpdate:modelValue": [
                  _cache[13] || (_cache[13] = ($event: any) => ((_unref(person).parentConsent) = $event)),
                  _cache[14] || (_cache[14] = ($event: any) => (_unref(person).parentConsent = $event))
                ],
                disabled: savingChanges.value || mode.value !== _unref(FormModes).add,
                entityId: __props.draw?.id || 0,
                entityTypeId: _unref(ConsentServiceEntityTypeIds).Application,
                details: __props.draw?.parentAttestation,
                mode: mode.value,
                required: mode.value === _unref(FormModes).add
              }, null, 8, ["modelValue", "disabled", "entityId", "entityTypeId", "details", "mode", "required"])
            ])
          ])
        ], 8, _hoisted_5),
        _withDirectives(_createElementVNode("button", {
          class: "submit",
          type: "submit",
          onClick: _withModifiers(submit, ["prevent"]),
          disabled: savingChanges.value
        }, " Submit ", 8, _hoisted_10), [
          [_vShow, mode.value === _unref(FormModes).edit]
        ])
      ], 512), [
        [_vShow, !gettingData.value]
      ]),
      _withDirectives(_createElementVNode("div", {
        class: "toggle",
        onClick: _cache[15] || (_cache[15] = ($event: any) => (showAdditionalInfo.value = !showAdditionalInfo.value))
      }, [
        _createTextVNode(_toDisplayString(showAdditionalInfo.value ? "Hide" : "View") + " Additional Information ", 1),
        _createElementVNode("span", {
          class: _normalizeClass([
            showAdditionalInfo.value ? 'icon-up-open-big' : 'icon-down-open-big',
          ])
        }, null, 2)
      ], 512), [
        [_vShow, !gettingData.value && mode.value === _unref(FormModes).view]
      ])
    ])
  ]))
}
}

})